// src/firebase/firebase.js  (plain JS)

import { Capacitor } from '@capacitor/core'
import { initializeApp } from 'firebase/app'
import {
  // ✅ use explicit Auth init with multi-persistence (safe across platforms)
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  // getAuth() will still work in other files; no need to import it here
} from 'firebase/auth'
import { getFirestore, initializeFirestore, memoryLocalCache } from 'firebase/firestore'
import { getFunctions } from 'firebase/functions'
import { getAnalytics } from 'firebase/analytics'

// ---- Config (from Vite env) ----
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
}

// ---- App ----
export const app = initializeApp(firebaseConfig)

// ---- Environment detection ----
export const isIOSCap = (Capacitor?.getPlatform?.() === 'ios')

// ---- Firestore (iOS-safe on Capacitor) ----
export const db = isIOSCap
  ? initializeFirestore(app, {
      localCache: memoryLocalCache(),
      experimentalForceLongPolling: true,
      useFetchStreams: false
    })
  : getFirestore(app)

// ---- Auth (explicit init with robust persistence) ----
// This ensures onAuthStateChanged / sign-in completes reliably in WKWebView.
export const auth = initializeAuth(app, {
  persistence: [
    indexedDBLocalPersistence,   // preferred where available
    browserLocalPersistence,     // fallback
    browserSessionPersistence,   // last resort
  ],
})
// Note: elsewhere, calling getAuth() will return this same instance.

// ---- Callable Functions (region optional via env, defaults to us-central1) ----
export const fns = getFunctions(app, import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'us-central1')

// ---- Analytics (web only; not in Capacitor WebView) ----
export let analytics = null
try {
  if (!isIOSCap) analytics = getAnalytics(app)
} catch {
  // ignore when not available
}
